<template>
  <div class="student-linked-page">
    <div v-if="loading" class="loading-block">
      <van-loading type="spinner" />
    </div>

    <template v-else>
      <div class="profile-card">
        <div class="profile-hero">
          <div class="profile-header">
            <div class="profile-avatar">{{ profileInitial }}</div>
            <div class="profile-main">
              <div class="profile-name-row">
                <div class="profile-name">{{ profileName }}</div>
                <div class="profile-badge">{{ statusText }}</div>
              </div>
              <div class="profile-subtitle">{{ profileSubtitle }}</div>
              <div v-if="hasArrearage" class="profile-arrearage">
                欠费：{{ arrearageAmountText }} 元
              </div>
            </div>
            <button v-if="props.target?.targetId" type="button" class="header-action" @click="handleAddSchedule">
              排课
            </button>
          </div>
          <div class="profile-metrics">
            <div class="metric-card primary">
              <div class="metric-label">剩余学费</div>
              <div class="metric-value">
                <span class="metric-number">{{ formatAmount(studentDetail?.surplusAmount) }}</span>
                <span class="metric-unit">元</span>
              </div>
            </div>
            <button type="button" class="metric-card neutral metric-button" @click="showHourBreakdown = true">
              <div class="metric-label-row">
                <div class="metric-label">剩余课时</div>
                <span class="metric-action">查看构成</span>
              </div>
              <div class="metric-value">
                <span class="metric-number">{{ totalRemainingClassHour }}</span>
                <span class="metric-unit">课时</span>
              </div>
            </button>
          </div>
        </div>

      </div>

      <div class="workspace-card">
        <van-tabs v-model:active="activeTab" class="student-tabs">
          <van-tab title="课表" name="schedule">
            <StudentScheduleTab v-if="activeTab === 'schedule'" :student-id="props.target?.targetId" />
          </van-tab>
          <van-tab title="剩余课时" name="hours">
            <StudentHoursTab v-if="activeTab === 'hours'" :student-id="props.target?.targetId" />
          </van-tab>
        </van-tabs>
      </div>

      <van-popup v-model:show="showHourBreakdown" round closeable position="bottom" class="hour-popup">
        <div class="hour-popup-content">
          <div class="hour-popup-title">剩余课时构成</div>
          <div class="hour-popup-total">
            <span class="hour-popup-total-label">总剩余课时</span>
            <strong class="hour-popup-total-value">{{ totalRemainingClassHour }} 课时</strong>
          </div>
          <div class="hour-breakdown-list">
            <div class="hour-breakdown-item">
              <span class="hour-breakdown-label">一对一课时</span>
              <strong class="hour-breakdown-value">{{ classHourSummary.oneToOneClassHour }} 课时</strong>
            </div>
            <div class="hour-breakdown-item">
              <span class="hour-breakdown-label">一对二课时</span>
              <strong class="hour-breakdown-value">{{ classHourSummary.oneToTwoClassHour }} 课时</strong>
            </div>
            <div class="hour-breakdown-item">
              <span class="hour-breakdown-label">小组课课时</span>
              <strong class="hour-breakdown-value">{{ classHourSummary.groupClassHour }} 课时</strong>
            </div>
            <div class="hour-breakdown-item">
              <span class="hour-breakdown-label">班课课时</span>
              <strong class="hour-breakdown-value">{{ classHourSummary.classesClassHour }} 课时</strong>
            </div>
          </div>
        </div>
      </van-popup>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { useDict } from '@/utils/dict'
import { getStudentDetail } from '@/api/crm'
import StudentHoursTab from './student-tabs/StudentHoursTab.vue'
import StudentScheduleTab from './student-tabs/StudentScheduleTab.vue'

const props = defineProps({
  target: {
    type: Object,
    default: null
  }
})

const { grade_list, student_status } = useDict(
  'grade_list',
  'student_status'
)

const router = useRouter()
const activeTab = ref('schedule')
const loading = ref(false)
const showHourBreakdown = ref(false)
const studentDetail = ref(null)
const pageScrollLockState = {
  htmlOverflow: '',
  htmlOverscrollBehavior: '',
  bodyOverflow: '',
  bodyOverscrollBehavior: ''
}

const profileName = computed(() => studentDetail.value?.studentName || props.target?.name || '-')
const profileInitial = computed(() => (profileName.value || '-').slice(0, 1))
const statusText = computed(() => getDictLabel(student_status.value, studentDetail.value?.status ?? props.target?.status) || '未知状态')
const gradeText = computed(() => getDictLabel(grade_list.value, studentDetail.value?.grade ?? props.target?.grade))
const plannerName = computed(() => studentDetail.value?.plannerName || props.target?.ownerName || '')
const schoolName = computed(() => studentDetail.value?.schoolName || props.target?.schoolName || '')
const profileSubtitle = computed(() => [gradeText.value, schoolName.value, plannerName.value].filter(Boolean).join(' / ') || '学员档案')
const arrearageAmountText = computed(() => formatAmount(studentDetail.value?.totalArrearageAmount))
const hasArrearage = computed(() => toNumber(studentDetail.value?.totalArrearageAmount) > 0)
const classHourSummary = computed(() => ({
  oneToOneClassHour: formatNumber(studentDetail.value?.studentClassHourSummary?.oneToOneClassHour),
  oneToTwoClassHour: formatNumber(studentDetail.value?.studentClassHourSummary?.oneToTwoClassHour),
  groupClassHour: formatNumber(studentDetail.value?.studentClassHourSummary?.groupClassHour),
  classesClassHour: formatNumber(studentDetail.value?.studentClassHourSummary?.classesClassHour)
}))
const totalRemainingClassHour = computed(() => formatNumber(
  toNumber(studentDetail.value?.studentClassHourSummary?.oneToOneClassHour)
  + toNumber(studentDetail.value?.studentClassHourSummary?.oneToTwoClassHour)
  + toNumber(studentDetail.value?.studentClassHourSummary?.groupClassHour)
  + toNumber(studentDetail.value?.studentClassHourSummary?.classesClassHour)
))

watch(() => props.target?.targetId, async studentId => {
  studentDetail.value = null
  if (!studentId) {
    return
  }
  loading.value = true
  try {
    const detail = await getStudentDetail(studentId)
    studentDetail.value = detail
  } catch (error) {
    showToast(error?.message || '加载学员信息失败')
  } finally {
    loading.value = false
  }
}, { immediate: true })

onMounted(() => {
  setPageScrollLocked(true)
})

onBeforeUnmount(() => {
  setPageScrollLocked(false)
})

function getDictLabel(list, value) {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  const matched = list.find(item => String(item.dictValue) === String(value))
  return matched ? matched.dictLabel : String(value)
}

function setPageScrollLocked(locked) {
  if (typeof document === 'undefined') {
    return
  }

  const { documentElement, body } = document
  if (locked) {
    pageScrollLockState.htmlOverflow = documentElement.style.overflow
    pageScrollLockState.htmlOverscrollBehavior = documentElement.style.overscrollBehavior
    pageScrollLockState.bodyOverflow = body.style.overflow
    pageScrollLockState.bodyOverscrollBehavior = body.style.overscrollBehavior
    documentElement.style.overflow = 'hidden'
    documentElement.style.overscrollBehavior = 'none'
    body.style.overflow = 'hidden'
    body.style.overscrollBehavior = 'none'
    return
  }

  documentElement.style.overflow = pageScrollLockState.htmlOverflow
  documentElement.style.overscrollBehavior = pageScrollLockState.htmlOverscrollBehavior
  body.style.overflow = pageScrollLockState.bodyOverflow
  body.style.overscrollBehavior = pageScrollLockState.bodyOverscrollBehavior
}

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isNaN(number) ? 0 : number
}

function formatNumber(value) {
  const number = toNumber(value)
  return Number.isInteger(number) ? String(number) : number.toFixed(2)
}

function formatAmount(value) {
  return toNumber(value).toFixed(2)
}

function handleAddSchedule() {
  if (!props.target?.targetId) {
    return
  }
  router.push({
    path: '/addStudentSchedule',
    query: {
      studentId: props.target.targetId
    }
  })
}

</script>

<style scoped>
.student-linked-page {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: calc(100vh - 48px);
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
  overscroll-behavior: none;
}

.loading-block {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
}

.header-action {
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
}

.profile-card,
.workspace-card {
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}

.profile-card {
  background: linear-gradient(180deg, #f5f8ff 0%, #edf3ff 100%);
}

.profile-hero {
  padding: 14px 16px;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.profile-avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb 0%, #60a5fa 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  flex-shrink: 0;
}

.profile-main {
  min-width: 0;
  flex: 1;
}

.profile-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.profile-name {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  letter-spacing: 0.01em;
}

.profile-badge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #166534;
  background: rgba(220, 252, 231, 0.95);
  border: 1px solid rgba(134, 239, 172, 0.8);
}

.profile-subtitle {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.profile-arrearage {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  margin-top: 8px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: #b45309;
  background: rgba(254, 243, 199, 0.9);
  border: 1px solid rgba(251, 191, 36, 0.45);
}

.metric-card {
  position: relative;
  overflow: hidden;
  min-height: 76px;
  padding: 14px 14px 12px;
  grid-column: span 2;
  border-radius: 18px;
  border: none;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
}

.metric-card::after {
  content: '';
  position: absolute;
  inset: auto -24px -24px auto;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
}

.metric-card.wide {
  grid-column: span 3;
}

.metric-card.primary {
  color: #1d4ed8;
}

.metric-card.neutral {
  color: #0f172a;
}

.metric-label {
  position: relative;
  z-index: 1;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.metric-label-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.metric-value {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 10px;
}

.metric-number {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.metric-unit {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.86;
}

.metric-button {
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.metric-action {
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  white-space: nowrap;
}

.workspace-card {
  padding: 12px 0 10px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.student-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.student-tabs :deep(.van-tabs) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.student-tabs :deep(.van-tabs__wrap) {
  padding: 0 16px;
  flex-shrink: 0;
}

.student-tabs :deep(.van-tabs__nav--line) {
  padding: 4px;
  border-radius: 16px;
  background: #f6f8fc;
}

.profile-metrics {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
}

.student-tabs :deep(.van-tab) {
  flex: 1;
  min-width: 0;
  height: 36px;
  color: #64748b;
  font-size: 14px;
  transition: all 0.2s ease;
}

.student-tabs :deep(.van-tab--active) {
  color: #0f172a;
  font-weight: 600;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.student-tabs :deep(.van-tabs__line) {
  display: none;
}

.student-tabs :deep(.van-tabs__content) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
}

.student-tabs :deep(.van-tab__panel) {
  padding: 16px 16px 6px;
  box-sizing: border-box;
  min-height: auto;
}

.hour-popup {
  overflow: hidden;
}

.hour-popup-content {
  padding: 28px 18px 24px;
}

.hour-popup-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.hour-popup-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(219, 234, 254, 0.92) 0%, rgba(255, 255, 255, 0.98) 100%);
  border: 1px solid rgba(96, 165, 250, 0.22);
}

.hour-popup-total-label,
.hour-breakdown-label {
  font-size: 13px;
  color: #64748b;
}

.hour-popup-total-value,
.hour-breakdown-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.hour-breakdown-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.hour-breakdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

@media (max-width: 480px) {
  .profile-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metric-card,
  .metric-card.wide {
    grid-column: span 1;
    min-height: 72px;
  }

  .metric-number {
    font-size: 20px;
  }
}
</style>
